import { useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Browser mic recording + Whisper transcription hook.
 * Returns { recording, transcribing, error, start, stop, cancel }
 * onTranscript(text) is called with the transcript after stop() finishes.
 */
export function useRecorder({ onTranscript, language = "en" } = {}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState("");
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const cleanup = useCallback(() => {
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch (e) { console.warn("mic cleanup failed", e); }
    streamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const start = useCallback(async () => {
    setError("");
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError("Voice input isn't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        cleanup();
        setRecording(false);
        if (!blob.size) {
          setError("No audio was captured. Please try again.");
          return;
        }
        setTranscribing(true);
        try {
          const form = new FormData();
          const filename = `speech.${(mr.mimeType || "audio/webm").includes("webm") ? "webm" : "wav"}`;
          form.append("file", blob, filename);
          const { data } = await api.post("/transcribe", form, {
            params: { language },
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
          });
          const text = (data && data.text) || "";
          if (text && onTranscript) onTranscript(text);
        } catch (e) {
          console.error("transcribe failed", e);
          setError(e?.response?.data?.detail || "Could not transcribe. Please try again.");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      console.error("mic access failed", e);
      setError("Microphone permission was denied.");
      cleanup();
    }
  }, [cleanup, language, onTranscript]);

  const stop = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      try { mr.stop(); } catch (e) { console.warn("mr.stop failed", e); }
    } else {
      cleanup();
      setRecording(false);
    }
  }, [cleanup]);

  const cancel = useCallback(() => {
    const mr = mediaRecorderRef.current;
    chunksRef.current = [];
    if (mr && mr.state !== "inactive") {
      try { mr.stop(); } catch (e) { console.warn("mr.cancel failed", e); }
    }
    cleanup();
    setRecording(false);
    setTranscribing(false);
    setError("");
  }, [cleanup]);

  return { recording, transcribing, error, start, stop, cancel };
}
